// src/pages/Tools/components/ToolRelatedCard.tsx
// Replaces: .related-tool-card, .related-tool-icon, .tool-link-arrow

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface ToolRelatedCardProps {
    /** Route path this card links to */
    to: string;
    /** Icon element shown in the 48px badge */
    icon: React.ReactNode;
    /** Card title */
    title: string;
    /** Card description */
    description: string;
    /** Link label (default: "Try it now") */
    linkLabel?: string;
    className?: string;
}

/*
 * .related-tool-card:
 *   padding: 24px; background: white; border: 1px solid #dadce0;
 *   border-radius: 12px; text-decoration: none; color: inherit;
 *   box-shadow: sm shadow; transition: all 0.2s;
 *   display: flex; flex-direction: column;
 *   :hover → transform: translateY(-4px); box-shadow: md; border-color: #1a73e8;
 *   :hover .tool-link-arrow → gap: 8px;
 *
 * .related-tool-icon:
 *   display: inline-flex; align-items: center; justify-content: center;
 *   width: 48px; height: 48px; background: #e8f0fe; color: #1a73e8;
 *   border-radius: 12px; margin-bottom: 16px;
 *
 * .related-tool-card h3:
 *   font-size: 1.125rem; font-weight: 500; color: #202124; margin: 0 0 12px;
 *
 * .related-tool-card p:
 *   font-size: 0.875rem; color: #5f6368; margin: 0 0 16px; line-height: 1.6; flex: 1;
 *
 * .tool-link-arrow:
 *   display: inline-flex; align-items: center; gap: 4px;
 *   font-size: 0.875rem; font-weight: 500; color: #1a73e8;
 *   transition: gap 0.2s;
 */
const ToolRelatedCard: React.FC<ToolRelatedCardProps> = ({
    to,
    icon,
    title,
    description,
    linkLabel = 'Try it now',
    className,
}) => (
    <Link
        to={to}
        className={cn(
            'group',
            'p-[24px] bg-white border border-[#dadce0] rounded-[12px]',
            'no-underline text-inherit',
            'shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)]',
            'transition-all duration-200',
            'flex flex-col',
            'hover:-translate-y-[4px]',
            'hover:shadow-[0_1px_3px_0_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)]',
            'hover:border-[#1a73e8]',
            className,
        )}
    >
        {/* Icon badge */}
        <div className="inline-flex items-center justify-center w-[48px] h-[48px] bg-[#e8f0fe] text-[#1a73e8] rounded-[12px] mb-[16px]">
            {icon}
        </div>

        <h3 className="text-[1.125rem] font-medium text-[#202124] m-0 mb-[12px]">
            {title}
        </h3>
        <p className="text-[0.875rem] text-[#5f6368] m-0 mb-[16px] leading-[1.6] flex-1">
            {description}
        </p>

        {/* Animated arrow link */}
        <span className="inline-flex items-center gap-[4px] group-hover:gap-[8px] text-[0.875rem] font-medium text-[#1a73e8] transition-[gap] duration-200">
            {linkLabel}
            <ArrowRight size={14} />
        </span>
    </Link>
);

export default ToolRelatedCard;
